import React from "react";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  ClockCircleOutlined,
  DownloadOutlined,
  LockOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import * as S from "./style";
import {
  SUNSET_DATES,
  daysUntil,
  formatLongDate,
  formatShortDate,
  getSunsetPhase,
  getTimelineProgress,
} from "./utils";
import { appRoutePaths } from "../../../../app/router/utils";
import { ReactComponent as BookIcon } from "../../../../assets/icons/book.svg";

type SunsetBannerProps = {
  linkCount: number;
  onExport: () => void;
  now?: Dayjs;
};

const pluralize = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? "" : "s"}`;

const SunsetBanner = ({ linkCount, onExport, now = dayjs() }: SunsetBannerProps) => {
  const navigate = useNavigate();
  const phase = getSunsetPhase(now);
  const variant = S.phaseVariant[phase];
  const { announced, linksClose, exportsEnd } = SUNSET_DATES;
  const closeDate = formatLongDate(linksClose);
  const daysToClose = daysUntil(linksClose, now);
  const activeLinks = pluralize(linkCount, "active link");

  const setUpKlashaPay = (kind: "brand" | "black") => (
    <S.ActionButton
      type="button"
      $kind={kind}
      $variant={variant}
      onClick={() => navigate(appRoutePaths.installation)}
    >
      <BookIcon aria-hidden /> Set up Klasha Pay
    </S.ActionButton>
  );

  const exportData = (kind: "outline" | "black") => (
    <S.ActionButton type="button" $kind={kind} $variant={variant} onClick={onExport}>
      <DownloadOutlined /> Export data
    </S.ActionButton>
  );

  const content = {
    announced: {
      icon: <ClockCircleOutlined />,
      title: `Payment link is closing on ${closeDate}`,
      body: `You can no longer create new links. Your ${activeLinks} keep accepting payments until ${closeDate}, 23:59 WAT. Move your collections to Klasha Pay or the Collections API before then.`,
      actions: <>{setUpKlashaPay("brand")}{exportData("outline")}</>,
      count: daysToClose,
      countLabel: "days until links close",
    },
    finalWeeks: {
      icon: <WarningOutlined />,
      title: `${pluralize(daysToClose, "day")} until Payment link closes`,
      body: `Your ${activeLinks} stop accepting payments on ${closeDate}, 23:59 WAT. Payments customers start before then will still settle to your wallet.`,
      actions: <>{setUpKlashaPay("brand")}{exportData("outline")}</>,
      count: daysToClose,
      countLabel: "days until links close",
    },
    closed: {
      icon: <LockOutlined />,
      title: `Payment link closed on ${closeDate}`,
      body: `Links no longer accept payments. Your link and payment history is read-only, and you can export it until ${formatLongDate(exportsEnd)}. Move your collections to Klasha Pay or the Collections API.`,
      actions: exportData("black"),
      count: daysUntil(exportsEnd, now),
      countLabel: "days left to export your data",
    },
    exportsEnded: {
      icon: <LockOutlined />,
      title: `Payment link closed on ${closeDate}`,
      body: "Links no longer accept payments. Your link and payment history is read-only. Move your collections to Klasha Pay or the Collections API.",
      actions: setUpKlashaPay("black"),
      count: linkCount,
      countLabel: "links deactivated",
    },
  }[phase];

  const milestones = [
    { label: "Announced", date: announced },
    { label: "Links close", date: linksClose },
    { label: "Exports end", date: exportsEnd },
  ];

  return (
    <S.Wrapper $variant={variant} role="status" data-testid="payment-link-sunset-banner">
      <S.Top>
        <S.Main>
          <S.IconBox $variant={variant} aria-hidden>
            {content.icon}
          </S.IconBox>
          <S.Content $variant={variant}>
            <h2>{content.title}</h2>
            <p>{content.body}</p>
            <S.Actions>{content.actions}</S.Actions>
          </S.Content>
        </S.Main>

        <S.Counter $variant={variant}>
          <strong>{content.count}</strong>
          <span>{content.countLabel}</span>
        </S.Counter>
      </S.Top>

      {phase !== "exportsEnded" && (
        <S.Timeline $variant={variant}>
          <S.Track $variant={variant} $progress={getTimelineProgress(now)} />
          <ol>
            {milestones.map(({ label, date }) => (
              <li key={label}>
                <S.Dot $variant={variant} $reached={!now.isBefore(date)} />
                <div>
                  <b>{label}</b>
                  <span> · {formatShortDate(date)}</span>
                </div>
              </li>
            ))}
          </ol>
        </S.Timeline>
      )}
    </S.Wrapper>
  );
};

export default SunsetBanner;
